import {ConnectionUserDto, WorkdayDto, WorkspaceReservationDto} from '@lib/infrastructure';
import {
  getBuildingById,
  getConnectionWhereaboutsForDay,
  getDefaultBuildingId,
  getTotalConnections,
  getWorkdayByDate,
  useAppSelector,
} from '@lib/store';
import {groupBy} from 'lodash';

export type ConnectionData = {
  user: ConnectionUserDto;
  workday: WorkdayDto;
  workspace: WorkspaceReservationDto | null;
};
export type ConnectionDataForBuilding = {buildingName: string; data: ConnectionData[]};

type RelevantConnections =
  | {status: 'Loading'; connections: null; buildingName: null; connectionInOfficeByBuilding: null}
  | {status: 'UserHasNoConnections'; connections: null; buildingName: string; connectionInOfficeByBuilding: null}
  | {
      status: 'NoOneThere';
      connections: [];
      buildingName: string;
      connectionInOfficeByBuilding: ConnectionDataForBuilding[];
    }
  | {
      status: 'Done';
      connections: ConnectionData[];
      buildingName: string;
      connectionInOfficeByBuilding: ConnectionDataForBuilding[];
    };

// CONVERT
export const useRelevantConnections = (dateKey: string): RelevantConnections => {
  const defaultBuildingId = useAppSelector(getDefaultBuildingId);
  const workday = useAppSelector((state) => getWorkdayByDate(state, dateKey)) ?? null;
  const buildingId = workday?.nodeId ?? defaultBuildingId;

  const connectionsToday = useAppSelector((state) => getConnectionWhereaboutsForDay(state, dateKey));

  const hasConnections = useAppSelector(getTotalConnections) > 0;

  const workdayLoading = useAppSelector((state) => state.data.workday.isLoading);
  const connectionsLoading = connectionsToday === null;

  const buildingName =
    useAppSelector((state) => getBuildingById(state, buildingId))?.name ?? workday?.nodeName ?? 'Unknown office';

  if (workdayLoading || connectionsLoading) {
    return {
      status: 'Loading',
      connections: null,
      buildingName: null,
      connectionInOfficeByBuilding: null,
    };
  }

  if (!hasConnections) {
    return {
      status: 'UserHasNoConnections',
      connections: null,
      buildingName,
      connectionInOfficeByBuilding: null,
    };
  }

  const connectionsInYourOffice = connectionsToday.inSomeOffice.filter((r) => r.workday.nodeId === buildingId);

  const connectionInOfficeGroupedByBuilding = groupBy(connectionsToday.inSomeOffice, (item) => item.workday.nodeName);
  const connectionInOfficeByBuilding = Object.entries(connectionInOfficeGroupedByBuilding).map(([building, data]) => ({
    buildingName: building,
    data,
  }));

  if (connectionsInYourOffice.length === 0) {
    return {
      status: 'NoOneThere',
      connections: [],
      buildingName,
      connectionInOfficeByBuilding,
    };
  }

  return {
    status: 'Done',
    connections: connectionsInYourOffice,
    buildingName,
    connectionInOfficeByBuilding,
  };
};
